import React, { FunctionComponent, useImperativeHandle, MouseEvent, RefAttributes, RefObject } from 'react';

// Utilities
import classnames from 'classnames';

// Styles
import './Scroller.module.scss';

export interface ScrollerElement {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;

    scrollToTop(): void;
    scrollToBottom(): void;
    scrollTo(position: number): void;
}

interface ScrollerProps {
    className?: string;
    style?: React.CSSProperties;
    scrollBehavior?: 'auto' | 'smooth';

    onTop?: () => void;
    onBottom?: () => void;
    onScroll?: (event: MouseEvent<HTMLDivElement>, scrollTop: string | number) => void;
}

const Scroller: FunctionComponent<ScrollerProps & RefAttributes<ScrollerElement>> = React.forwardRef((props, ref) => {
    const scrollerRef: RefObject<HTMLDivElement> = React.createRef();
    const className = classnames(['scroller', props.className]);

    useImperativeHandle(ref, () => ({
        scrollTop: scrollerRef.current.scrollTop,
        scrollHeight: scrollerRef.current.scrollHeight,
        clientHeight: scrollerRef.current.clientHeight,
        scrollTo: (number) => {
            scrollerRef.current.scrollTop = number;
        },
        scrollToTop: () => {
            scrollerRef.current.scrollTop = 0;
        },
        scrollToBottom: () => {
            scrollerRef.current.scrollTo(0, scrollerRef.current.scrollHeight);
        },
    }));

    const handleScroll = (event) => {
        const scrollHeight = event.target.scrollHeight;
        const clientHeight = event.target.clientHeight;
        const scrollTop = event.target.scrollTop;

        if (props.onScroll) {
            props.onScroll(event, scrollTop);
        }

        if (scrollTop <= 0 && props.onTop) {
            props.onTop();
        }

        if (scrollTop + clientHeight >= scrollHeight && props.onBottom) {
            props.onBottom();
        }
    };

    return (
        <div ref={scrollerRef} className={className} onScroll={handleScroll} style={{ scrollBehavior: props.scrollBehavior, ...props.style }}>
            {props.children}
        </div>
    );
});

Scroller.defaultProps = {
    style: {},
    className: '',
    scrollBehavior: 'auto',
};

export default Scroller;
